/**
 * Secure Key Caching Service
 * Uses WebCrypto API and localStorage for secure storage of ElGamal keypairs
 * Keys are encrypted and stored in localStorage (persists across browser refreshes)
 */

const CACHE_PREFIX = 'ConfidentialBalances_';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

class SecureKeyCache {
    constructor() {
        this.cryptoKey = null;
    }

    /**
     * Initialize the secure key cache
     */
    async initialize() {
        try {
            // Check if we have an encryption key
            const hasEncryptionKey = !!localStorage.getItem('secureCacheKey');
            
            // Generate or retrieve the encryption key
            this.cryptoKey = await this.getOrCreateCryptoKey();
            
            // If we just generated a new encryption key (didn't have one before),
            // we need to clear any old encrypted keypairs that were encrypted with a different key
            // This handles the migration from sessionStorage to localStorage
            if (!hasEncryptionKey) {
                console.log('[KeyCache] New encryption key generated - clearing old encrypted keypairs');
                // Clear all cached keypairs since they were encrypted with a different key
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(CACHE_PREFIX)) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => localStorage.removeItem(key));
                console.log('[KeyCache] Cleared', keysToRemove.length, 'old encrypted keypairs');
            }
            
            console.log('Secure key cache initialized (localStorage)');
            return true;
        } catch (error) {
            console.error('Failed to initialize secure key cache:', error);
            return false;
        }
    }

    /**
     * Get or create the encryption key for secure storage
     */
    async getOrCreateCryptoKey() {
        try {
            // Try to import existing key from localStorage
            const storedKeyData = localStorage.getItem('secureCacheKey');
            if (storedKeyData) {
                console.log('[KeyCache] Found existing encryption key in localStorage, length:', storedKeyData.length);
                try {
                    const keyData = JSON.parse(storedKeyData);
                    console.log('[KeyCache] Parsed key data type:', Array.isArray(keyData) ? 'Array' : typeof keyData);
                    
                    // keyData should be an array from Array.from(exportedKey)
                    // Convert to Uint8Array directly (it's already an array of numbers)
                    let keyBytes;
                    if (Array.isArray(keyData)) {
                        keyBytes = new Uint8Array(keyData);
                    } else {
                        // Fallback: try Object.values if it's an object
                        keyBytes = new Uint8Array(Object.values(keyData));
                    }
                    
                    console.log('[KeyCache] Key bytes length:', keyBytes.length, 'Expected: 32 (256 bits)');
                    if (keyBytes.length !== 32) {
                        throw new Error(`Invalid key length: ${keyBytes.length}, expected 32`);
                    }
                    
                    const cryptoKey = await crypto.subtle.importKey(
                        'raw',
                        keyBytes,
                        { name: 'AES-GCM' },
                        false,
                        ['encrypt', 'decrypt']
                    );
                    console.log('[KeyCache] ✓ Successfully imported existing encryption key');
                    return cryptoKey;
                } catch (importError) {
                    console.error('[KeyCache] ✗ Error importing key data:', importError);
                    console.error('[KeyCache] Import error details:', {
                        name: importError.name,
                        message: importError.message,
                        stack: importError.stack
                    });
                    console.error('[KeyCache] Key data preview:', storedKeyData.substring(0, 200));
                    // If import fails, we'll generate a new key below
                    // But first, clear the corrupted key data
                    localStorage.removeItem('secureCacheKey');
                    console.log('[KeyCache] Removed corrupted encryption key from localStorage');
                }
            } else {
                console.log('[KeyCache] ✗ No existing encryption key found in localStorage');
                console.log('[KeyCache] This means keys will be regenerated and old encrypted keys cannot be decrypted');
            }
        } catch (error) {
            console.error('[KeyCache] Error accessing localStorage for encryption key:', error);
            console.log('[KeyCache] Generating new encryption key...');
        }

        // Generate new encryption key
        console.log('[KeyCache] Generating new encryption key...');
        const cryptoKey = await crypto.subtle.generateKey(
            { name: 'AES-GCM', length: 256 },
            true, // extractable for storage
            ['encrypt', 'decrypt']
        );

        // Export and store the key in localStorage (persists across refreshes)
        try {
            const exportedKey = await crypto.subtle.exportKey('raw', cryptoKey);
            // exportedKey is an ArrayBuffer, convert to Uint8Array then to array
            const keyBytes = new Uint8Array(exportedKey);
            const keyArray = Array.from(keyBytes);
            localStorage.setItem('secureCacheKey', JSON.stringify(keyArray));
            console.log('[KeyCache] ✓ New encryption key generated and stored in localStorage');
            console.log('[KeyCache] Stored key length:', keyArray.length, 'bytes');
        } catch (storeError) {
            console.error('[KeyCache] Failed to store encryption key:', storeError);
            // Continue anyway - the key is in memory
        }

        return cryptoKey;
    }

    /**
     * Store a keypair securely
     */
    async storeKeypair(account, chainId, verifyingContract, keypair) {
        if (!this.cryptoKey) {
            throw new Error('Secure key cache not initialized');
        }

        try {
            // Encrypt the keypair data
            const keypairData = JSON.stringify({
                public_key: keypair.public_key,
                private_key: keypair.private_key,
                timestamp: Date.now()
            });

            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encryptedData = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                this.cryptoKey,
                new TextEncoder().encode(keypairData)
            );

            // Store in localStorage (persists across browser refreshes)
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            const cacheEntry = {
                encryptedData: Array.from(new Uint8Array(encryptedData)),
                iv: Array.from(iv),
                timestamp: Date.now()
            };

            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
            
            console.log('[KeyCache] ✓ Keypair stored securely in localStorage with key:', cacheKey);
            return true;
        } catch (error) {
            console.error('Failed to store keypair:', error);
            return false;
        }
    }

    /**
     * Retrieve a keypair securely
     */
    async retrieveKeypair(account, chainId, verifyingContract) {
        if (!this.cryptoKey) {
            console.error('[KeyCache] Crypto key not initialized');
            throw new Error('Secure key cache not initialized');
        }

        try {
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            console.log('[KeyCache] Looking for cache key:', cacheKey);
            const storedData = localStorage.getItem(cacheKey);
            
            if (!storedData) {
                console.log('[KeyCache] No data found in localStorage for key:', cacheKey);
                // Debug: list all keys with our prefix
                const allKeys = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(CACHE_PREFIX)) {
                        allKeys.push(key);
                    }
                }
                console.log('[KeyCache] Available cache keys:', allKeys);
                return null;
            }

            console.log('[KeyCache] Found cached data, parsing...');
            const result = JSON.parse(storedData);

            // Check if cache entry is not too old (24 hours)
            const age = Date.now() - result.timestamp;
            const ageHours = age / (60 * 60 * 1000);
            console.log('[KeyCache] Cache entry age:', ageHours.toFixed(2), 'hours');
            if (age > MAX_AGE) {
                // Cache expired, remove it
                console.log('[KeyCache] Cache expired, removing...');
                await this.removeKeypair(account, chainId, verifyingContract);
                return null;
            }

            // Decrypt the keypair data
            console.log('[KeyCache] Decrypting cached keypair...');
            console.log('[KeyCache] Decryption params:', {
                encryptedDataLength: result.encryptedData.length,
                ivLength: result.iv.length,
                hasCryptoKey: !!this.cryptoKey
            });
            
            const encryptedData = new Uint8Array(result.encryptedData);
            const iv = new Uint8Array(result.iv);
            
            let decryptedData;
            try {
                decryptedData = await crypto.subtle.decrypt(
                    { name: 'AES-GCM', iv },
                    this.cryptoKey,
                    encryptedData
                );
            } catch (decryptError) {
                console.error('[KeyCache] ✗ Decryption failed:', decryptError);
                console.error('[KeyCache] Decrypt error details:', {
                    name: decryptError.name,
                    message: decryptError.message,
                    stack: decryptError.stack
                });
                // This usually means the encryption key doesn't match (was regenerated)
                console.error('[KeyCache] This usually means the encryption key was regenerated and cannot decrypt old keys');
                throw decryptError;
            }

            const keypairData = JSON.parse(new TextDecoder().decode(decryptedData));
            
            console.log('[KeyCache] ✓ Keypair successfully retrieved and decrypted from localStorage');
            return {
                public_key: keypairData.public_key,
                private_key: keypairData.private_key
            };
        } catch (error) {
            console.error('[KeyCache] ✗ Failed to decrypt cached keypair:', error);
            console.error('[KeyCache] Error details:', {
                name: error.name,
                message: error.message || '(empty message)',
                stack: error.stack,
                account: account ? `${account.slice(0, 6)}...${account.slice(-4)}` : null,
                chainId,
                verifyingContract
            });
            // Remove corrupted cache entry
            await this.removeKeypair(account, chainId, verifyingContract);
            return null;
        }
    }

    /**
     * Remove a cached keypair
     */
    async removeKeypair(account, chainId, verifyingContract) {
        try {
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            localStorage.removeItem(cacheKey);
            
            console.log('Keypair removed from localStorage cache');
            return true;
        } catch (error) {
            console.error('Failed to remove keypair:', error);
            return false;
        }
    }

    /**
     * Clear all cached keypairs (but preserve connection history)
     */
    async clearAll() {
        try {
            // Clear all keys with our prefix, but preserve connection markers
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    // Don't remove connection markers - they track first-time vs subsequent connections
                    if (!key.includes('connected_')) {
                        keysToRemove.push(key);
                    }
                }
            }
            
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Also remove the encryption key
            localStorage.removeItem('secureCacheKey');
            
            console.log('All cached keypairs cleared from localStorage (connection history preserved)');
            return true;
        } catch (error) {
            console.error('Failed to clear cache:', error);
            return false;
        }
    }

    /**
     * Clear all cached keypairs AND connection history (full reset)
     */
    async clearAllIncludingHistory() {
        try {
            // Clear all keys with our prefix including connection markers
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            
            keysToRemove.forEach(key => localStorage.removeItem(key));
            
            // Also remove the encryption key
            localStorage.removeItem('secureCacheKey');
            
            console.log('All cached keypairs and connection history cleared from localStorage');
            return true;
        } catch (error) {
            console.error('Failed to clear cache:', error);
            return false;
        }
    }

    /**
     * Get cache statistics
     */
    async getCacheStats() {
        try {
            let totalEntries = 0;
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    totalEntries++;
                }
            }
            
            return {
                totalEntries,
                storageType: 'localStorage',
                prefix: CACHE_PREFIX
            };
        } catch (error) {
            console.error('Failed to get cache stats:', error);
            return null;
        }
    }

    /**
     * Check if a wallet+account combination has been connected before
     * This tracks first-time vs subsequent connections
     * Synchronous check for immediate use
     */
    hasBeenConnectedBefore(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const connectionKey = `${CACHE_PREFIX}connected_${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            return !!localStorage.getItem(connectionKey);
        } catch (error) {
            console.error('Failed to check connection history:', error);
            return false;
        }
    }

    /**
     * Check if keys are cached (synchronous check of localStorage)
     * This is a quick check to see if keys exist without async operations
     */
    hasCachedKeysSync(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            return !!localStorage.getItem(cacheKey);
        } catch (error) {
            console.error('Failed to check cached keys:', error);
            return false;
        }
    }

    /**
     * Mark a wallet+account combination as having been connected
     * This persists across sessions to track first-time vs subsequent connections
     */
    markAsConnected(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const connectionKey = `${CACHE_PREFIX}connected_${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            localStorage.setItem(connectionKey, JSON.stringify({ timestamp: Date.now() }));
            console.log('[KeyCache] Marked wallet+account as connected:', connectionKey);
            return true;
        } catch (error) {
            console.error('Failed to mark connection:', error);
            return false;
        }
    }
}

// Export singleton instance
export const secureKeyCache = new SecureKeyCache();
